import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, FileText, Printer, Receipt, CalendarPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PageHeader, StatusBadge, EmptyState } from "../components/Shared";
import { api, eur, fmtDate, formatApiError } from "../lib/api";
import { QUOTE_STATUS } from "../lib/labels";

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [status, setStatus] = useState("all");
  const navigate = useNavigate();
  const load = () => api.get("/quotes").then((r) => setQuotes(r.data));
  useEffect(() => {
    load();
  }, []);

  const action = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const list = quotes.filter((q) => status === "all" || q.status === status);

  return (
    <div className="fade-up">
      <PageHeader overline="Commercial" title="Devis" subtitle="Créez des devis avec estimation IA du volume m³.">
        <Button asChild data-testid="new-quote-button" className="bg-[#0b2c6b] hover:bg-[#1559d6]"><Link to="/app/devis/nouveau"><Plus className="mr-2 h-4 w-4" />Nouveau devis</Link></Button>
      </PageHeader>
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger data-testid="quotes-status-filter" className="mb-5 w-52 bg-white"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">Tous les devis</SelectItem>{Object.entries(QUOTE_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
      </Select>
      {list.length === 0 ? <EmptyState icon={FileText} text="Aucun devis" testid="quotes-empty" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <Table data-testid="quotes-table">
            <TableHeader><TableRow><TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead>Date</TableHead><TableHead>Volume</TableHead><TableHead>Total TTC</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {list.map((q) => (
                <TableRow key={q.id} className="cursor-pointer" data-testid={`quote-row-${q.id}`} onClick={() => navigate(`/app/devis/${q.id}`)}>
                  <TableCell className="font-mono-num text-xs font-semibold">{q.number}</TableCell>
                  <TableCell className="font-medium">{q.client_name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{fmtDate(q.move_date)}</TableCell>
                  <TableCell className="font-mono-num text-sm">{q.volume_m3} m³</TableCell>
                  <TableCell className="font-mono-num text-sm font-semibold">{eur(q.total_ttc)}</TableCell>
                  <TableCell><StatusBadge map={QUOTE_STATUS} value={q.status} /></TableCell>
                  <TableCell className="whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                    <Button size="icon" variant="ghost" title="Imprimer / PDF" data-testid={`print-quote-${q.id}`} onClick={() => navigate(`/app/devis/${q.id}/imprimer`)}><Printer className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" title="Facturer" data-testid={`invoice-quote-${q.id}`} onClick={() => action(() => api.post(`/quotes/${q.id}/invoice`), "Facture créée")}><Receipt className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" title="Planifier" data-testid={`schedule-quote-${q.id}`} onClick={() => action(() => api.post(`/quotes/${q.id}/schedule`), "Déménagement ajouté au planning")}><CalendarPlus className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" title="Supprimer" data-testid={`delete-quote-${q.id}`} onClick={() => window.confirm("Supprimer ce devis ?") && action(() => api.delete(`/quotes/${q.id}`), "Devis supprimé")}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
